import { isDefined } from "@clipboard-health/util-ts";
import { useGetQualifications } from "@src/appV2/Admin/Qualifications/api/useGetQualifications";
import { type Qualification } from "@src/appV2/Admin/Qualifications/types";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";

import { getWorkplaceTypesWithQualifications } from "../utils/getWorkplaceTypesWithQualifications";
import { useGetWorkplaceTypes } from "./useGetWorkplaceTypes";

interface GetQualificationsForWorkplaceTypeArguments {
  readonly workplaceTypeName?: string;
  readonly workplaceId?: string;
}

interface UseGetQualificationsForWorkplaceTypeResult {
  qualifications: Qualification[];
  isLoading: boolean;
}

export function useGetQualificationsForWorkplaceType(
  parameters: Readonly<GetQualificationsForWorkplaceTypeArguments>
): UseGetQualificationsForWorkplaceTypeResult {
  const { workplaceTypeName, workplaceId } = parameters;
  const { data: workplaceTypes, isLoading: isLoadingWorkplaceTypes } = useGetWorkplaceTypes();

  const isCustomQualificationsEnabled =
    useCbhFlag(CbhFeatureFlag.CUSTOM_QUALIFICATIONS_ENABLED, {
      defaultValue: false,
    }) ?? false;

  const shouldFetchCustomQualifications = isCustomQualificationsEnabled && isDefined(workplaceId);

  const { data: qualificationsData, isLoading: isLoadingQualifications } = useGetQualifications(
    {
      filter: {
        workplaceId,
      },
    },
    { enabled: shouldFetchCustomQualifications }
  );

  const workplaceTypeQualifications =
    workplaceTypes && workplaceTypeName
      ? (getWorkplaceTypesWithQualifications(workplaceTypes).find(
          ({ attributes }) => attributes.name === workplaceTypeName
        )?.qualifications ?? [])
      : [];

  const customQualifications = shouldFetchCustomQualifications
    ? (qualificationsData?.data.filter(
        (qualification) => qualification.attributes.workplaceId === workplaceId
      ) ?? [])
    : [];

  const qualifications = [...workplaceTypeQualifications, ...customQualifications];

  const isLoading =
    isLoadingWorkplaceTypes || (shouldFetchCustomQualifications && isLoadingQualifications);

  return { qualifications, isLoading };
}
